import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { StyledAdminUsersListHeader } from './styles';
import { AdminListCheckbox } from '../AdminListCheckbox';
import { AdminListHeaderCell } from '../AdminListHeaderCell';
import { IAdminUser, TSortAdminUserByHeaderTitle } from 'types';
import { AdminCalendar } from '../AdminCalendar';
import { AdminFilterSelect } from '../AdminFilterSelect';

interface IUsersListHeaderProps {
  children?: React.ReactNode;
  onSelectAll: (selected: boolean) => void;
  selectedAll?: boolean;
  onSort: (sortBy: TSortAdminUserByHeaderTitle) => void;
  sortBy: string | null;
  sortDir: 'ASC' | 'DESC' | 'NONE';
  onFilterByDate: (filterByDate: {
    filterBy: keyof IAdminUser;
    fromDate: Date;
    toDate: Date;
  }) => void;
  onFilterByValue: (filterByValue: { filterBy: keyof IAdminUser; filterValue: string }) => void;
}

type FieldType = {
  key: TSortAdminUserByHeaderTitle;
  sortable?: boolean;
  filterable?: boolean;
  filterWithDate?: boolean;
  options?: string[];
};

export const headerFields: FieldType[] = [
  { key: 'id', sortable: true },
  { key: 'email', sortable: true },
  { key: 'cost', sortable: true },
  { key: 'adjust', sortable: true },
  { key: 'link' },
  { key: 'wallet' },
  { key: 'vip', filterable: true, options: ['-', 'vip', 'not vip'] },
  {
    key: 'averageDailyHashRate',
    filterable: true,
    options: ['0', '<1000', '>1000<3000', '>3000<10000', '>10000<30000', '>30000<100000'],
  },
  {
    key: 'averageWeeklyHashRate',
    filterable: true,
    options: ['0', '<1000', '>1000<3000', '>3000<10000', '>10000<30000', '>30000<100000'],
  },
  { key: 'createdAt', filterable: true, filterWithDate: true },
  { key: 'updatedAt', filterable: true, filterWithDate: true },
  { key: 'comment' },
];

export const AdminUsersListHeader = ({
  onSort,
  onSelectAll,
  onFilterByDate,
  onFilterByValue,
  selectedAll,
  sortBy,
  sortDir,
}: IUsersListHeaderProps) => {
  const { t } = useTranslation();

  const selectRowHandler = () => {
    onSelectAll(!selectedAll);
  };

  const renderFilter = (
    key: TSortAdminUserByHeaderTitle,
    withDate?: boolean,
    options?: string[],
  ) => {
    return withDate
      ? () => (
          <AdminCalendar
            onChange={(dateRange: { fromDate: Date; toDate: Date }) =>
              onFilterByDate({
                filterBy: key,
                fromDate: dateRange.fromDate,
                toDate: dateRange.toDate,
              })
            }
          />
        )
      : options
        ? () => (
            <AdminFilterSelect
              onSelect={(value) => onFilterByValue({ filterBy: key, filterValue: value })}
              filterName={key}
              optionsList={options}
            />
          )
        : undefined;
  };

  return (
    <StyledAdminUsersListHeader>
      <div className="admin-list-header-cell-container select">
        <AdminListCheckbox checked={selectedAll} onChange={selectRowHandler} />
      </div>
      {headerFields.map(({ key, sortable, filterable, filterWithDate, options }) => (
        <div key={key} className={cn('admin-list-header-cell-container', key.toLowerCase())}>
          <AdminListHeaderCell
            fieldKey={key}
            filter={filterable}
            sort={sortable}
            onSort={sortable ? () => onSort(key) : undefined}
            onFilter={filterable ? renderFilter(key, filterWithDate, options) : undefined}
            isActive={sortBy === key}
            sortDir={sortBy === key ? sortDir : 'NONE'}
          >
            {t(`adminPage.editUsers.listHeaders.${key}`)}
          </AdminListHeaderCell>
        </div>
      ))}
    </StyledAdminUsersListHeader>
  );
};
